import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["list", "input", "button", "submit", "error", "total", "passing", "percent", "timeMode", "duration", "durationInfo", "identityOption", "identityError"];
  static values = { url: String };

  initialize() {
    this._rerollTimer = null;
    this._rerollGeneration = 0;
    this._previewSynchronized = false;
  }

  connect() {
    this.updatePassingBoundsAndPercent();
    this.updateDurationUI();
    this.setPreviewSynchronized(this.previewMatchesCurrentTotal());
  }

  disconnect() {
    if (this._rerollTimer) window.clearTimeout(this._rerollTimer);
    this._rerollTimer = null;
    this.invalidateReroll();
  }

  validateIdentity(event) {
    if (!this.hasIdentityOptionTarget || this.identityOptionTargets.some((input) => input.checked)) {
      if (this.hasIdentityErrorTarget) this.identityErrorTarget.classList.add("hidden");
      return;
    }
    event.preventDefault();
    if (this.hasIdentityErrorTarget) this.identityErrorTarget.classList.remove("hidden");
    this.identityOptionTarget.focus();
  }

  reroll() {
    if (this._rerollTimer) window.clearTimeout(this._rerollTimer);
    this._rerollTimer = null;
    this.invalidateReroll();
    return this.performReroll(this.validTotal());
  }

  async performReroll(requestedTotal) {
    if (!this.hasUrlValue) return;
    if (requestedTotal === null) return;

    const requestGeneration = ++this._rerollGeneration;
    this.setPreviewSynchronized(false);
    this.setLoading(true);
    this.hideError();

    try {
      const url = new URL(this.urlValue, window.location.origin);
      url.searchParams.set("total_questions", String(requestedTotal));

      const response = await fetch(url.toString(), {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      if (requestGeneration !== this._rerollGeneration || this.validTotal() !== requestedTotal) return;

      const questionIds = Array.isArray(data.question_ids) ? data.question_ids : [];
      if (questionIds.length !== requestedTotal) {
        this.showError("Nie udało się przygotować pełnego zestawu pytań. Spróbuj ponownie.");
        return;
      }

      this.inputTarget.value = JSON.stringify(questionIds);
      this.listTarget.innerHTML = data.html || "";
      this.setPreviewSynchronized(true);
      this.updatePassingBoundsAndPercent();
    } catch (error) {
      if (requestGeneration !== this._rerollGeneration || this.validTotal() !== requestedTotal) return;

      console.error("Preview reroll failed:", error);
      this.showError("Nie udało się wylosować nowego zestawu. Spróbuj ponownie.");
    } finally {
      if (requestGeneration === this._rerollGeneration) this.setLoading(false);
    }
  }

  onTotalInput() {
    this.setPreviewSynchronized(false);
    const total = this.validTotal();

    if (total !== null && this.selectedTimeMode() !== "no_limit" && this.hasDurationTarget) {
      this.durationTarget.value = String(total * 2);
      this.updateDurationUI();
    }

    this.updatePassingBoundsAndPercent();
  }

  onTotalChange() {
    this.onTotalInput();
    const total = this.validTotal();

    if (this._rerollTimer) window.clearTimeout(this._rerollTimer);
    this._rerollTimer = null;
    this.invalidateReroll();
    if (total === null) return;

    this._rerollTimer = window.setTimeout(() => {
      this._rerollTimer = null;
      this.performReroll(total);
    }, 250);
  }

  onPassingInput() {
    this.updatePassingBoundsAndPercent();
  }

  onPassingChange() {
    this.onPassingInput();
  }

  validTotal() {
    if (!this.hasTotalTarget || this.totalTarget.value === "") return null;

    const total = Number(this.totalTarget.value);
    if (!Number.isInteger(total) || total < 5 || total > 50) return null;

    return total;
  }

  validPassing(total = this.validTotal()) {
    if (total === null || !this.hasPassingTarget || this.passingTarget.value === "") return null;

    const passing = Number(this.passingTarget.value);
    if (!Number.isInteger(passing) || passing < 1 || passing > total) return null;

    return passing;
  }

  currentTotal() {
    if (!this.hasTotalTarget) return 10;
    const raw = Number.parseInt(this.totalTarget.value, 10);
    if (!Number.isFinite(raw)) return 10;
    return Math.min(50, Math.max(5, raw));
  }

  updatePassingBoundsAndPercent() {
    if (!(this.hasTotalTarget && this.hasPassingTarget)) return;

    const total = this.validTotal();
    if (total !== null) this.passingTarget.max = String(total);

    const passing = this.validPassing(total);
    if (this.hasPercentTarget) {
      this.percentTarget.textContent = passing === null
        ? "—"
        : `${((passing / total) * 100).toFixed(1)}%`;
    }
  }

  onTimeModeChange() {
    this.updateDurationUI();
  }

  onDurationChange() {
    this.updateDurationUI();
  }

  currentDurationMinutes() {
    if (!this.hasDurationTarget) return this.currentTotal() * 2;
    const raw = Number.parseInt(this.durationTarget.value, 10);
    if (!Number.isFinite(raw) || raw <= 0) return this.currentTotal() * 2;
    return raw;
  }

  selectedTimeMode() {
    if (!this.hasTimeModeTarget) return "limited";
    const selected = this.timeModeTargets.find((el) => el.checked);
    return selected?.value || "limited";
  }

  updateDurationUI() {
    if (!this.hasDurationTarget) return;

    const limited = this.selectedTimeMode() !== "no_limit";
    const total = this.currentTotal();

    if (!limited) {
      this.durationTarget.disabled = true;
      this.durationTarget.classList.add("bg-gray-100", "text-gray-400", "cursor-not-allowed");
      if (this.hasDurationInfoTarget) {
        this.durationInfoTarget.textContent = "(Bez limitu czasu dla całego egzaminu)";
      }
      return;
    }

    this.durationTarget.disabled = false;
    this.durationTarget.classList.remove("bg-gray-100", "text-gray-400", "cursor-not-allowed");

    let minutes = this.currentDurationMinutes();
    if (!Number.isFinite(minutes) || minutes <= 0) {
      minutes = total * 2;
    }
    this.durationTarget.value = String(minutes);

    if (this.hasDurationInfoTarget) {
      const avg = total > 0 ? (minutes / total).toFixed(1) : "0.0";
      this.durationInfoTarget.textContent = `(Średnio: ${avg} min na pytanie)`;
    }
  }

  setLoading(isLoading) {
    if (!this.hasButtonTarget) return;
    this.buttonTarget.disabled = isLoading;
    this.buttonTarget.classList.toggle("opacity-50", isLoading);
    this.buttonTarget.classList.toggle("cursor-not-allowed", isLoading);
  }

  invalidateReroll() {
    this._rerollGeneration += 1;
    this.setPreviewSynchronized(false);
    this.setLoading(false);
  }

  previewMatchesCurrentTotal() {
    const total = this.validTotal();
    if (total === null || !this.hasInputTarget) return false;

    try {
      const questionIds = JSON.parse(this.inputTarget.value || "[]");
      return Array.isArray(questionIds) && questionIds.length === total;
    } catch (_error) {
      return false;
    }
  }

  setPreviewSynchronized(isSynchronized) {
    this._previewSynchronized = isSynchronized;
    this.updateSubmitState();
  }

  updateSubmitState() {
    if (!this.hasSubmitTarget) return;

    const waitingForPreview = this.validTotal() !== null && !this._previewSynchronized;
    this.submitTargets.forEach((submit) => {
      submit.disabled = waitingForPreview;
      submit.classList.toggle("opacity-50", waitingForPreview);
      submit.classList.toggle("cursor-not-allowed", waitingForPreview);
    });
  }

  showError(message) {
    if (!this.hasErrorTarget) return;
    this.errorTarget.textContent = message;
    this.errorTarget.classList.remove("hidden");
  }

  hideError() {
    if (!this.hasErrorTarget) return;
    this.errorTarget.textContent = "";
    this.errorTarget.classList.add("hidden");
  }
}
